/**
 * Links into the document generator (/generate) and parsing of its query parameters.
 *
 * Query parameters (all optional, applied once when the page loads):
 *   templateId     - preselect the template (and skip to the sources step)
 *   projectId      - preselect the project
 *   customerId     - filter the project picker to one customer
 *   requirementIds - comma-separated requirement ids to preselect (others start unticked)
 *   sessionIds     - comma-separated session ids to preselect
 */

/** Route of the document generator. */
export const DOCUMENT_GENERATOR_PATH = '/generate';

/** Upper bound on ids carried in a link (keeps URLs a sane length; larger picks mean "all"). */
export const MAX_LINKED_IDS = 400;

/**
 * Clean a list of ids: strings only, trimmed, no empties or commas, de-duplicated, in order.
 * @param {Iterable<unknown>|null|undefined} ids - Ids.
 * @returns {string[]} Clean ids.
 */
export function normalizeIdList(ids) {
  if (!ids) return [];
  const seen = new Set();
  const out = [];
  Array.from(ids).forEach((raw) => {
    if (raw == null) return;
    const id = String(raw).trim();
    if (!id || id.includes(',') || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

/**
 * Parse a comma-separated id list query value.
 * @param {string|null|undefined} value - Raw query value (e.g. "a,b,c").
 * @returns {string[]} Ids (empty when missing).
 */
export function parseIdListParam(value) {
  if (typeof value !== 'string' || !value.trim()) return [];
  return normalizeIdList(value.split(','));
}

/**
 * Path of the document generator with optional preselections.
 * @param {{templateId?: string|null, projectId?: string|null, customerId?: string|null, requirementIds?: Iterable<string>|null, sessionIds?: Iterable<string>|null}} [options] - Preselections.
 * @returns {string} Path such as "/generate?projectId=p1&requirementIds=r1,r2".
 */
export function documentGeneratorPath({ templateId, projectId, customerId, requirementIds, sessionIds } = {}) {
  const params = new URLSearchParams();
  if (customerId) params.set('customerId', customerId);
  if (projectId) params.set('projectId', projectId);
  if (templateId) params.set('templateId', templateId);
  const requirements = normalizeIdList(requirementIds);
  if (requirements.length && requirements.length <= MAX_LINKED_IDS) params.set('requirementIds', requirements.join(','));
  const sessions = normalizeIdList(sessionIds);
  if (sessions.length && sessions.length <= MAX_LINKED_IDS) params.set('sessionIds', sessions.join(','));
  // Keep the commas readable; ids never contain them (normalizeIdList drops any that do).
  const query = params.toString().replace(/%2C/gi, ',');
  return query ? `${DOCUMENT_GENERATOR_PATH}?${query}` : DOCUMENT_GENERATOR_PATH;
}

/**
 * Open the document generator in-app.
 * @param {Function} navigate - react-router `navigate`.
 * @param {Parameters<typeof documentGeneratorPath>[0]} [options] - Preselections.
 * @returns {string} The path that was opened.
 */
export function openDocumentGenerator(navigate, options = {}) {
  const path = documentGeneratorPath(options);
  navigate(path);
  return path;
}

/**
 * The preselected subset of `available` named by `requested`, or null when nothing usable was
 * requested (the caller then keeps its default, usually "everything").
 * @param {string[]} requested - Ids from the link.
 * @param {Array<{id: string}>} available - Items the project has.
 * @returns {Set<string>|null} Ids to select.
 */
export function pickRequestedIds(requested, available) {
  if (!requested?.length) return null;
  const known = new Set((available || []).map((item) => item.id));
  const picked = requested.filter((id) => known.has(id));
  return picked.length ? new Set(picked) : null;
}
